"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function HairAppointmentsSection({ section }) {
  const items = [
    {
      id: 1,
      title: "Blowout",
      image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=600&q=80",
      location: "Anita Wilson Hair Stylist"
    },
    {
      id: 2,
      title: "Haircut & Blowout",
      image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=600&q=80",
      location: "Anita Wilson Hair Stylist"
    },
    {
      id: 3,
      title: "Haircut & Color & Blowout",
      image: "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=600&q=80",
      location: "Anita Wilson Hair Stylist"
    },
    {
      id: 4,
      title: "Beard/Mustache Shaping",
      image: "https://images.unsplash.com/photo-1621605815971-fbc98d665033?auto=format&fit=crop&w=600&q=80",
      location: "Anita Wilson Hair Stylist"
    },
    {
      id: 5,
      title: "Shave",
      image: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&w=600&q=80",
      location: "Anita Wilson Hair Stylist"
    },
    {
      id: 6,
      title: "Men's cut",
      image: "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?auto=format&fit=crop&w=600&q=80",
      location: "Anita Wilson Hair Stylist"
    }
  ];

  const avatars = [
    "/odoo/unsplash/avatars/u1.jpg",
    "/odoo/unsplash/avatars/u2.jpg",
    "/odoo/unsplash/avatars/u3.jpg",
    "/odoo/unsplash/avatars/u4.jpg"
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Calendar Icon, Handwriting Title, and Copy */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8"
          >
            {/* Playful Blue Doodle Calendar Icon */}
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 select-none">
              <svg viewBox="0 0 80 80" fill="none" className="w-full h-full drop-shadow-md">
                {/* Background Shadow offset */}
                <rect x="14" y="22" width="54" height="46" rx="10" fill="#0077C5" />
                {/* Main Calendar Body */}
                <rect x="10" y="18" width="54" height="46" rx="10" fill="#00A09D" stroke="#0077C5" strokeWidth="3" />
                {/* Top Binder Loops */}
                <rect x="22" y="10" width="8" height="14" rx="4" fill="#00A09D" stroke="#0077C5" strokeWidth="3" />
                <rect x="46" y="10" width="8" height="14" rx="4" fill="#00A09D" stroke="#0077C5" strokeWidth="3" />
                {/* Calendar Grid Dots */}
                <circle cx="24" cy="36" r="3.5" fill="#FFFFFF" />
                <circle cx="37" cy="36" r="3.5" fill="#FFFFFF" />
                <circle cx="50" cy="36" r="3.5" fill="#FFFFFF" />
                <circle cx="24" cy="48" r="3.5" fill="#FFFFFF" />
                <circle cx="37" cy="48" r="3.5" fill="#FFFFFF" />
                <circle cx="50" cy="48" r="3.5" fill="#FFFFFF" />
              </svg>
            </div>

            {/* Handwritten Heading with Underline */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
                <span className="relative inline-block">
                  Appointments
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-3 text-[#00A09D] overflow-visible" 
                    viewBox="0 0 200 12" 
                    fill="none"
                  >
                    <path 
                      d="M2 8 C 60 2, 140 2, 198 8" 
                      stroke="#00A09D" 
                      strokeWidth="5" 
                      strokeLinecap="round" 
                    />
                  </svg>
                </span>
                <br />
                <span>done right</span>
              </h2>
            </div>

            {/* Text Paragraphs */}
            <div className="space-y-5 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Let clients schedule their appointments</strong> on their own... and reschedule easily. Enjoy the smooth integration with your Google Calendar, Outlook, or iOS.
              </p>
              <p>
                <strong className="text-white font-bold">Reduce no-shows</strong> with automated reminders and advance payments, and boost your online reviews with automated requests.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Appointment Booking Mockup Window */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden text-slate-800 transition-transform duration-300 hover:scale-[1.005]">
              
              {/* Mockup Header Navigation Bar */}
              <div className="bg-white border-b border-slate-100 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
                
                {/* Brand Scissors Logo & Navigation Links */}
                <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                  {/* Scissors Icon */}
                  <div className="text-slate-800 font-bold flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 0A3 3 0 104.879 4.879a3 3 0 004.242 4.242zm0 5.758a3 3 0 10-4.242 4.242 3 3 0 004.242-4.242z" />
                    </svg>
                  </div>

                  <nav className="hidden sm:flex items-center gap-3 md:gap-5 text-slate-600 font-medium text-xs">
                    <span className="hover:text-slate-900 cursor-pointer">Home</span>
                    <span className="hover:text-slate-900 cursor-pointer">Services</span>
                    <span className="hover:text-slate-900 cursor-pointer">Pricing</span>
                    <span className="hover:text-slate-900 cursor-pointer">About Us</span>
                    <span className="text-slate-900 font-bold border-b-2 border-slate-900 pb-0.5 cursor-pointer">Appointment</span>
                    <span className="hover:text-slate-900 cursor-pointer">Contact us</span>
                  </nav>
                </div>

                {/* Header Action Buttons */}
                <div className="flex items-center gap-2 sm:gap-3 ml-auto">
                  <button className="px-2.5 sm:px-3 py-1 text-slate-700 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 transition-colors">
                    Sign In
                  </button>
                  <button className="px-3 sm:px-4 py-1.5 text-white text-xs font-medium bg-[#875A7B] rounded-lg shadow-sm hover:bg-[#734b68] transition-colors">
                    Schedule an appointment
                  </button>
                </div>
              </div>

              {/* Mockup Main Body Area */}
              <div className="p-4 sm:p-6 bg-[#FAFAFA]">
                
                {/* Title & Search Bar Row */}
                <div className="flex items-center justify-between mb-5 gap-3">
                  <h3 className="font-serif text-slate-800 text-base sm:text-xl font-bold tracking-tight">
                    Choose your appointment
                  </h3>

                  {/* Search Input Box */}
                  <div className="relative w-32 sm:w-44">
                    <input 
                      type="text" 
                      placeholder="Search..." 
                      readOnly 
                      className="w-full text-xs px-3 py-1 pr-7 border border-slate-200 rounded-md text-slate-400 bg-white shadow-none focus:outline-none"
                    />
                    <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* 3x2 Grid of Appointment Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {items.map((item) => (
                    <div 
                      key={item.id} 
                      className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col transition-shadow hover:shadow-md"
                    >
                      {/* Image Thumbnail Container */}
                      <div className="relative h-28 sm:h-36 w-full bg-slate-100 overflow-hidden">
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Stacked Stylist Avatars at Bottom Left */}
                        <div className="absolute bottom-2 left-2 flex -space-x-2">
                          {avatars.map((av, idx) => (
                            <img 
                              key={idx}
                              src={av}
                              alt="Stylist avatar"
                              className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-white object-cover shadow-sm"
                            />
                          ))}
                        </div>
                      </div>

                      {/* Card Content Footer */}
                      <div className="p-3 flex-1 flex flex-col justify-between bg-white">
                        <h4 className="font-serif font-bold text-slate-800 text-xs sm:text-sm leading-snug line-clamp-2">
                          {item.title}
                        </h4>
                        
                        <div className="mt-1.5 flex items-center text-[10px] sm:text-xs text-slate-500 gap-1">
                          <svg className="w-3 h-3 text-slate-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <span className="truncate">{item.location}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Effortless PoS for busy salons
   ───────────────────────────────────────────── */
export function HairSalonPosSection({ section }) {
  const categories = [
    { name: "Equipment", bg: "bg-[#93C5FD] text-slate-900" },
    { name: "Hair Products", bg: "bg-[#A7F3D0] text-slate-900" },
    { name: "Misc", bg: "bg-[#7DD3FC] text-slate-900" },
    { name: "Services", bg: "bg-[#FBCFE8] text-slate-900" },
    { name: "Skincare", bg: "bg-[#BFDBFE] text-slate-900" }
  ];

  const products = [
    // Row 1
    { title: "Ashley Joy Strength Shampoo", bg: "bg-[#FEF08A]", image: "https://images.unsplash.com/photo-1535585209827-a15fcdbc4c2d?auto=format&fit=crop&w=300&q=80" },
    { title: "Blowout", bg: "bg-[#FBCFE8]", image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=300&q=80" },
    { title: "Brushing", bg: "bg-[#FBCFE8]", image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=300&q=80" },
    { title: "Color", bg: "bg-[#FBCFE8]", image: "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=300&q=80" },
    { title: "Cut", bg: "bg-[#FBCFE8]", image: "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?auto=format&fit=crop&w=300&q=80" },
    
    // Row 2
    { title: "Diva Edit Veloce 3800 Pro Hair Dryer...", bg: "bg-[#93C5FD]", image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=300&q=80" },
    { title: "Gift Card", bg: "bg-[#7DD3FC]", image: null, isGift: true },
    { title: "Keywest Professionals...", bg: "bg-[#A7F3D0]", image: "https://images.unsplash.com/photo-1535585209827-a15fcdbc4c2d?auto=format&fit=crop&w=300&q=80" },
    { title: "Keywest Professionals...", bg: "bg-[#A7F3D0]", image: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=300&q=80" },
    { title: "L'Oréal Professionnel...", bg: "bg-[#A7F3D0]", image: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?auto=format&fit=crop&w=300&q=80" },

    // Row 3
    { title: "L'Oréal Professionnel...", bg: "bg-[#A7F3D0]", image: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?auto=format&fit=crop&w=300&q=80" },
    { title: "L'Oréal Professionnel Seri...", bg: "bg-[#A7F3D0]", image: "https://images.unsplash.com/photo-1535585209827-a15fcdbc4c2d?auto=format&fit=crop&w=300&q=80" },
    { title: "Matrix SoColor Pre-Bonded Hair Color", bg: "bg-[#A7F3D0]", image: "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=300&q=80" },
    { title: "O3+ Bridal Facial Kit Radiant & Glowing...", bg: "bg-[#BFDBFE]", image: "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=300&q=80" },
    { title: "O3+ Bridal Facial Kit Vitamin-C Glowing...", bg: "bg-[#BFDBFE]", image: "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=300&q=80" }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Odoo POS UI Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden text-slate-800 transition-transform duration-300 hover:scale-[1.005]">
              
              {/* POS Header Bar */}
              <div className="bg-[#F8FAFC] border-b border-slate-200 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 text-xs">
                {/* Left controls */}
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded border border-slate-300 flex items-center justify-center text-slate-500 bg-white">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="px-2 py-0.5 bg-teal-50 border border-teal-200 text-teal-700 font-bold rounded text-[11px]">
                    004
                  </span>
                  <span className="font-bold text-[#875A7B] text-sm tracking-tight ml-1">
                    odoo
                  </span>
                </div>

                {/* Right controls */}
                <div className="flex items-center gap-2">
                  <div className="relative hidden sm:block">
                    <input 
                      type="text" 
                      placeholder="Search products..." 
                      readOnly 
                      className="w-36 sm:w-44 text-[11px] px-2.5 py-1 pl-7 border border-slate-200 rounded text-slate-400 bg-white"
                    />
                    <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  
                  {/* Cashier Badge */}
                  <div className="flex items-center gap-1.5 bg-white border border-slate-200 px-2 py-0.5 rounded">
                    <img src="/odoo/unsplash/avatars/u1.jpg" alt="Anita Wilson" className="w-4 h-4 rounded-full object-cover" />
                    <span className="text-[11px] font-medium text-slate-700">Anita Wilson</span>
                  </div>

                  <div className="w-5 h-5 flex flex-col justify-center gap-0.5 text-slate-600 cursor-pointer">
                    <span className="h-0.5 w-full bg-slate-600 rounded"></span>
                    <span className="h-0.5 w-full bg-slate-600 rounded"></span>
                    <span className="h-0.5 w-full bg-slate-600 rounded"></span>
                  </div>
                </div>
              </div>

              {/* Main Body: Left Cart & Right Product Catalog */}
              <div className="grid grid-cols-12 min-h-[380px] bg-slate-50">
                
                {/* Left Panel: Cart & Checkout */}
                <div className="col-span-5 sm:col-span-4 border-r border-slate-200 bg-white flex flex-col justify-between p-2.5 sm:p-3 text-[11px]">
                  {/* Itemized Cart List */}
                  <div className="space-y-2.5 overflow-y-auto max-h-[220px] scrollbar-thin">
                    {/* Item 1 */}
                    <div className="flex justify-between items-start pb-1.5 border-b border-slate-100">
                      <div>
                        <div className="font-bold text-slate-800 text-[11px] sm:text-xs">Cut and Color</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          <span className="font-bold text-slate-700">1.00</span> x $ 143.75 / Units
                        </div>
                      </div>
                      <div className="font-semibold text-slate-700 text-xs">$ 143.75</div>
                    </div>

                    {/* Item 2 */}
                    <div className="flex justify-between items-start pb-1.5 border-b border-slate-100">
                      <div>
                        <div className="font-bold text-slate-800 text-[11px] sm:text-xs">Matrix SoColor Pre-Bonded Hair Color</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          <span className="font-bold text-slate-700">1.00</span> x $ 28.75 / Units
                        </div>
                      </div>
                      <div className="font-semibold text-slate-700 text-xs">$ 28.75</div>
                    </div>

                    {/* Item 3 */}
                    <div className="flex justify-between items-start pb-1.5 border-b border-slate-100">
                      <div>
                        <div className="font-bold text-slate-800 text-[11px] sm:text-xs">O3+ Bridal Facial Kit Radiant & Glowing Skin</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          <span className="font-bold text-slate-700">2.00</span> x $ 11.50 / Units
                        </div>
                      </div>
                      <div className="font-semibold text-slate-700 text-xs">$ 23.00</div>
                    </div>
                  </div>

                  {/* Summary Footer */}
                  <div className="pt-2 border-t border-slate-200 space-y-1.5 mt-2">
                    <div className="flex justify-between text-slate-500 text-[10px]">
                      <span>Taxes</span>
                      <span>$ 25.50</span>
                    </div>

                    <div className="flex justify-between items-baseline font-bold text-slate-900 text-xs sm:text-sm">
                      <span>Total</span>
                      <span className="text-sm sm:text-base">$ 195.50</span>
                    </div>

                    <div className="flex justify-between text-[10px] text-slate-500">
                      <span>Points</span>
                      <span className="text-teal-600 font-semibold">+1 = 1</span>
                    </div>

                    {/* Action buttons */}
                    <div className="grid grid-cols-3 gap-1 pt-1">
                      <button className="bg-teal-50 text-teal-700 border border-teal-200 text-[9px] sm:text-[10px] font-semibold py-1 rounded truncate">
                        Grace Turner
                      </button>
                      <button className="bg-slate-100 text-slate-600 text-[9px] sm:text-[10px] py-1 rounded truncate">
                        Internal Note
                      </button>
                      <button className="bg-slate-100 text-slate-600 text-[9px] sm:text-[10px] py-1 rounded truncate">
                        Actions
                      </button>
                    </div>

                    {/* Main Payment CTA Button */}
                    <button className="w-full py-2 bg-[#875A7B] text-white font-bold text-xs sm:text-sm rounded-lg shadow hover:bg-[#734b68] transition-colors text-center mt-1">
                      Payment
                    </button>
                  </div>
                </div>

                {/* Right Panel: Product Catalog Grid */}
                <div className="col-span-7 sm:col-span-8 p-2.5 sm:p-3 flex flex-col justify-between">
                  
                  {/* Category Pills Header */}
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-2 scrollbar-none text-[10px]">
                    {categories.map((cat, idx) => (
                      <span 
                        key={idx}
                        className={`px-2 py-0.5 rounded font-medium shrink-0 cursor-pointer ${cat.bg}`}
                      >
                        {cat.name}
                      </span>
                    ))}
                  </div>

                  {/* 5-Column Product Items Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
                    {products.map((prod, idx) => (
                      <div 
                        key={idx}
                        className="bg-white rounded border border-slate-200 overflow-hidden flex flex-col justify-between shadow-2xs hover:shadow-xs transition-shadow"
                      >
                        {/* Product Header Tile */}
                        <div className={`text-[9px] sm:text-[10px] font-semibold p-1 leading-tight truncate ${prod.bg}`}>
                          {prod.title}
                        </div>

                        {/* Product Image / Icon */}
                        <div className="h-12 sm:h-14 w-full bg-slate-50 flex items-center justify-center overflow-hidden">
                          {prod.isGift ? (
                            <div className="bg-slate-900 text-white font-bold text-[9px] px-2 py-0.5 rounded shadow-xs">
                              GIFT
                            </div>
                          ) : (
                            <img 
                              src={prod.image} 
                              alt={prod.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

          {/* Right Column: Title, Icon, Highlights & Copy */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-4 sm:space-y-6"
          >
            {/* Doodle Arrow pointing down to Smiley Icon */}
            <div className="relative flex flex-col items-center select-none">
              {/* Curved Doodle Arrow SVG */}
              <div className="w-8 h-8 ml-6 text-slate-400">
                <svg viewBox="0 0 40 40" fill="none" className="w-full h-full stroke-current">
                  <path d="M12 4 C 18 14, 22 20, 24 30 M 18 26 L 24 30 L 28 24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {/* Yellow Smiley Face Doodle Icon */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#FBBF24] border-3 border-slate-900 flex items-center justify-center shadow-lg transform -rotate-3">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <circle cx="9" cy="9.5" r="1.3" fill="currentColor" />
                  <circle cx="15" cy="9.5" r="1.3" fill="currentColor" />
                  <path d="M7 13.8 C 9.5 17.5, 14.5 17.5, 17 13.8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
                <span className="relative inline-block">
                  Effortless PoS for 
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-3 text-[#00A09D] overflow-visible" 
                    viewBox="0 0 200 12" 
                    fill="none"
                  >
                    <path 
                      d="M2 8 C 60 2, 140 2, 198 8" 
                      stroke="#f9f046ff" 
                      strokeWidth="5" 
                      strokeLinecap="round" 
                    />
                  </svg>
                </span>
                <br />
                <span>busy salons</span>
              </h2>
            </div>

            {/* Text Paragraphs */}
            <div className="space-y-5 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Easily add extra services</strong> such as conditioning, scalp massages, and other treatments when checking out.
              </p>
              <p>
                <strong className="text-white font-bold">Customizable loyalty programs.</strong> You decide how clients earn loyalty points and what they can use them for - discounts, freebies, and more!
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Custom website builder
   ───────────────────────────────────────────── */
export function HairSalonWebsiteBuilderSection({ section }) {
  const serviceCards = [
    {
      title: "Styling & Blowouts",
      desc: "Experience the luxury of our professional blowout service, where every brushstroke unveils the true beauty and glamour of your hair.",
      image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=600&q=80",
      isSelected: false
    },
    {
      title: "Color",
      desc: "Transform your hair into a vibrant masterpiece with our expert color services, adding a touch of personality to every strand.",
      image: "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=600&q=80",
      isSelected: false
    },
    {
      title: "Men's Cuts & Shaves",
      desc: "Elevate your style and confidence with our modern and traditional haircuts, shaves, and facial hair shaping services.",
      image: "https://images.unsplash.com/photo-1621605815971-fbc98d665033?auto=format&fit=crop&w=600&q=80",
      isSelected: true
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        
        {/* Centered Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Custom </span>
            <span className="relative inline-block px-1">
              website
              <svg 
                className="absolute -bottom-2 left-0 w-full h-4 text-[#00A09D] overflow-visible" 
                viewBox="0 0 100 12" 
                fill="none"
              >
                <path 
                  d="M 0 6 Q 12 0, 25 6 T 50 6 T 75 6 T 100 6" 
                  stroke="#00A09D" 
                  strokeWidth="4" 
                  strokeLinecap="round" 
                  fill="none" 
                />
              </svg>
            </span>
            <span> builder</span>
          </h2>

          <p className="text-slate-300 text-lg sm:text-xl lg:text-2xl font-normal max-w-3xl mx-auto leading-relaxed">
            Drag-and-drop simplicity meets professional web designer results on all your devices.
          </p>
        </motion.div>

        {/* Dual Mockup Showcase (Desktop Editor + Mobile Preview) */}
        <div className="relative max-w-[1300px] mx-auto flex flex-col lg:flex-row items-center lg:items-start justify-center gap-6 lg:gap-8">
          
          {/* Green Cursor Doodle at bottom left */}
          <div className="absolute -bottom-8 -left-6 sm:-left-10 z-30 select-none pointer-events-none transform -rotate-12 hidden sm:block">
            <svg className="w-12 h-12 text-[#00A09D] drop-shadow-md" viewBox="0 0 40 40" fill="currentColor" stroke="#000000" strokeWidth="2">
              <path d="M6 4 L28 18 L18 20 L24 34 L18 36 L12 22 L6 26 Z" />
            </svg>
          </div>

          {/* Desktop Website Builder Window */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[75%] bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden text-slate-800"
          >
            <div className="grid grid-cols-12 min-h-[460px]">
              
              {/* Left 8 Cols: Website Canvas & Page Content */}
              <div className="col-span-12 sm:col-span-8 flex flex-col bg-white border-r border-slate-200">
                
                {/* Navbar Inside Web Builder Preview */}
                <div className="border-b border-slate-100 px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2 text-[11px] sm:text-xs bg-white">
                  <div className="flex items-center gap-3 sm:gap-4 overflow-x-auto">
                    {/* Scissors Logo */}
                    <svg className="w-4 h-4 text-slate-800 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 0A3 3 0 104.879 4.879a3 3 0 004.242 4.242zm0 5.758a3 3 0 10-4.242 4.242 3 3 0 004.242-4.242z" />
                    </svg>

                    <nav className="hidden sm:flex items-center gap-2.5 text-slate-600 text-[11px] font-medium">
                      <span>Home</span>
                      <span className="text-slate-900 font-bold border-b border-slate-900 pb-0.5">Services</span>
                      <span>Pricing</span>
                      <span>About Us</span>
                      <span>Appointment</span>
                      <span>Contact us</span>
                    </nav>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] font-medium text-slate-700 hidden sm:inline">Anita Wilson ▾</span>
                    <button className="px-2.5 py-1 text-white text-[10px] sm:text-[11px] font-medium bg-[#875A7B] rounded-md shadow-xs">
                      Schedule an appointment
                    </button>
                  </div>
                </div>

                {/* Page Hero Banner inside Canvas */}
                <div className="bg-[#6B6E70] text-white px-4 sm:px-6 py-6 sm:py-8">
                  <h3 className="font-serif text-xl sm:text-3xl font-bold tracking-tight">
                    Services
                  </h3>
                </div>

                {/* 3 Columns Service Cards Grid inside Canvas */}
                <div className="p-3 sm:p-5 bg-slate-50 flex-1 grid grid-cols-3 gap-2.5 sm:gap-3">
                  {serviceCards.map((card, idx) => (
                    <div
                      key={idx}
                      className={`relative bg-white rounded-lg p-2 sm:p-2.5 flex flex-col justify-between transition-all ${
                        card.isSelected 
                          ? "ring-2 ring-cyan-500 shadow-md scale-[1.02]" 
                          : "border border-slate-200/80 shadow-xs"
                      }`}
                    >
                      {/* Selection Handles & Action bar for Selected Card */}
                      {card.isSelected && (
                        <>
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E11D48] text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1 shadow-xs z-20">
                            <span>‹</span>
                            <span>+</span>
                            <span>🗑</span>
                          </div>
                          {/* Corner handles */}
                          <span className="w-2 h-2 bg-white border border-cyan-500 rounded-full absolute -top-1 -left-1 z-20"></span>
                          <span className="w-2 h-2 bg-white border border-cyan-500 rounded-full absolute -top-1 -right-1 z-20"></span>
                          <span className="w-2 h-2 bg-white border border-cyan-500 rounded-full absolute -bottom-1 -left-1 z-20"></span>
                          <span className="w-2 h-2 bg-white border border-cyan-500 rounded-full absolute -bottom-1 -right-1 z-20"></span>
                        </>
                      )}

                      {/* Card Image */}
                      <div className="h-20 sm:h-28 w-full bg-slate-100 rounded overflow-hidden mb-2">
                        <img 
                          src={card.image} 
                          alt={card.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Card Info */}
                      <div>
                        <h4 className="font-serif font-bold text-slate-800 text-[10px] sm:text-xs leading-snug mb-1">
                          {card.title}
                        </h4>
                        <p className="text-[8px] sm:text-[10px] text-slate-500 leading-tight line-clamp-3">
                          {card.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

              </div>

              {/* Right 4 Cols: Odoo Web Builder Customizer Inspector Sidebar */}
              <div className="col-span-12 sm:col-span-4 bg-[#202229] text-slate-300 p-2.5 sm:p-3 text-[10px] sm:text-[11px] flex flex-col justify-between">
                
                <div className="space-y-3">
                  {/* Top Editor Toolbar */}
                  <div className="flex items-center justify-between pb-2 border-b border-slate-700/60 text-[10px]">
                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="cursor-pointer hover:text-white">↺</span>
                      <span className="cursor-pointer hover:text-white">↻</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-400 text-[9px]">Discard</span>
                      <button className="bg-[#875A7B] text-white px-2 py-0.5 rounded font-medium text-[9px]">
                        Save
                      </button>
                    </div>
                  </div>

                  {/* Main Sidebar Navigation Tabs */}
                  <div className="grid grid-cols-3 text-center border-b border-slate-700/60 text-[10px] font-medium pb-1.5">
                    <span className="text-slate-400 cursor-pointer">BLOCKS</span>
                    <span className="text-cyan-400 font-bold border-b-2 border-cyan-400 pb-1">CUSTOMIZE</span>
                    <span className="text-slate-400 cursor-pointer">THEME</span>
                  </div>

                  {/* Inspector Controls */}
                  <div className="space-y-2.5 text-[10px] text-slate-300">
                    
                    {/* Columns Section */}
                    <div>
                      <div className="font-bold text-slate-400 uppercase text-[9px] mb-1">Columns</div>
                      <div className="flex items-center justify-between text-slate-400 bg-[#17181D] p-1.5 rounded">
                        <span>Layout</span>
                        <div className="flex gap-1 text-[9px]">
                          <span className="bg-slate-700 px-1 rounded text-white">Grid</span>
                          <span className="px-1">Cols: 3</span>
                        </div>
                      </div>
                    </div>

                    {/* Card Properties Section */}
                    <div className="space-y-1.5 pt-1 border-t border-slate-700/40">
                      <div className="font-bold text-slate-400 uppercase text-[9px]">Card</div>
                      
                      <div className="flex items-center justify-between text-slate-400">
                        <span>Border</span>
                        <span className="text-slate-200">1 px</span>
                      </div>

                      <div className="flex items-center justify-between text-slate-400">
                        <span>Round Corners</span>
                        <span className="text-slate-200">6.4 px</span>
                      </div>

                      <div className="flex items-center justify-between text-slate-400">
                        <span>Ratio</span>
                        <span className="text-slate-200">Square</span>
                      </div>
                    </div>

                    {/* Inline Text Controls */}
                    <div className="pt-1 border-t border-slate-700/40 space-y-1">
                      <div className="font-bold text-slate-400 uppercase text-[9px]">Inline Text</div>
                      <div className="bg-[#17181D] p-1.5 rounded flex flex-wrap gap-1.5 text-slate-300 text-[10px]">
                        <span className="font-bold cursor-pointer">B</span>
                        <span className="italic cursor-pointer">I</span>
                        <span className="underline cursor-pointer">U</span>
                        <span className="line-through cursor-pointer">S</span>
                        <span className="ml-auto text-cyan-400 font-semibold cursor-pointer">+ AI</span>
                      </div>
                    </div>

                  </div>
                </div>

              </div>

            </div>
          </motion.div>

          {/* Right Mobile Phone Device Frame */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="w-56 sm:w-64 bg-white rounded-3xl sm:rounded-[32px] shadow-[0_25px_60px_rgba(0,0,0,0.5)] border-4 border-slate-100 overflow-hidden shrink-0 text-slate-800 font-sans"
          >
            {/* Mobile Top Bar */}
            <div className="bg-slate-900 text-white px-3 py-1 flex items-center justify-between text-[9px]">
              <span>9:41</span>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>
                <img src="/odoo/unsplash/avatars/u1.jpg" alt="User" className="w-3.5 h-3.5 rounded-full object-cover" />
              </div>
            </div>

            {/* Mobile Web Header */}
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between text-xs bg-white">
              <svg className="w-4 h-4 text-slate-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 0A3 3 0 104.879 4.879a3 3 0 004.242 4.242zm0 5.758a3 3 0 10-4.242 4.242 3 3 0 004.242-4.242z" />
              </svg>
              <div className="w-4 h-4 flex flex-col justify-center gap-0.5 text-slate-700">
                <span className="h-0.5 w-full bg-slate-700 rounded"></span>
                <span className="h-0.5 w-full bg-slate-700 rounded"></span>
                <span className="h-0.5 w-full bg-slate-700 rounded"></span>
              </div>
            </div>

            {/* Mobile Hero Header */}
            <div className="bg-[#6B6E70] text-white px-3 py-4">
              <h4 className="font-serif text-lg font-bold">Services</h4>
            </div>

            {/* Mobile Single Column Card Preview */}
            <div className="p-3 bg-slate-50">
              <div className="bg-white rounded-lg p-2.5 border border-slate-200 shadow-xs">
                <div className="h-28 w-full bg-slate-100 rounded overflow-hidden mb-2">
                  <img 
                    src={serviceCards[0].image} 
                    alt={serviceCards[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h5 className="font-serif font-bold text-slate-800 text-xs mb-1">
                  {serviceCards[0].title}
                </h5>
                <p className="text-[10px] text-slate-500 leading-tight">
                  {serviceCards[0].desc}
                </p>
              </div>
            </div>

          </motion.div>

        </div>

      </div>
    </section>
  );
}

export default HairAppointmentsSection;
